// ============================================================================
// COMPANIES DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for company management
// ============================================================================

import { ApiClient } from '../../core/apiClient';

interface Company {
  id: number;
  name: string;
  legal_name?: string;
  registration_number?: string;
  industry?: string;
  size?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UpdateCompanyRequest {
  name?: string;
  legal_name?: string;
  registration_number?: string;
  industry?: string;
  size?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  is_active?: boolean;
}

interface CompanyFramework {
  assignment_id: number;
  framework_version_id?: number;
  assignment_status?: 'pending' | 'active' | 'completed' | 'archived';
  id: number;
  name: string;
  description?: string;
  assigned_at: string;
  total_pages: number;
  not_started_count: number;
  in_progress_count: number;
  submitted_count: number;
  approved_count: number;
  certified_count?: number;
  certification_id?: number | null;
  certificate_number?: string | null;
  certification_status?: 'active' | 'expired' | 'revoked' | 'suspended' | null;
  certification_issued_at?: string | null;
  certification_expires_at?: string | null;
}

interface CompanyDashboardStats {
  total_users: number;
  active_frameworks: number;
  total_pages: number;
  submitted_pages: number;
  approved_pages: number;
  pending_reviews: number;
  overdue_tasks: number;
}

interface SubmittedPage {
  id: number;
  framework_id: number;
  section: string;
  title: string;
  description?: string;
  status: string;
  assigned_to: number;
  company_id: number;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  due_date?: string;
  priority: string;
  first_name: string;
  last_name: string;
  email: string;
  evidence_files?: string;
}

interface CompliancePageForReview {
  id: number;
  framework_id: number;
  section: string;
  title: string;
  description?: string;
  status: string;
  assigned_to: number;
  company_id: number;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  due_date?: string;
  priority: string;
  framework_name: string;
  first_name: string;
  last_name: string;
  email: string;
  evidence_files?: string;
}

class CompaniesApi extends ApiClient {
  constructor() {
    super({
      baseURL: '/api',
      enableLogging: import.meta.env.DEV,
    });
  }

  async getCompanies(params?: {
    limit?: number;
    offset?: number;
    page?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<Company[]> {
    const response = await this.client.get('/companies', {
      params: {
        page: 1,
        limit: 500,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...params,
      },
    });
    return response.data.data || response.data || [];
  }

  async getCompany(id: number): Promise<Company> {
    const response = await this.client.get(`/companies/${id}`);
    return response.data.data || response.data;
  }

  async updateCompany(id: number, data: UpdateCompanyRequest): Promise<void> {
    console.log('Client: Sending updateCompany request', { id, data: JSON.stringify(data) });
    await this.client.put(`/companies/${id}`, data);
  }

  async createCompany(data: Partial<Company>): Promise<{ id: number }> {
    const response = await this.client.post('/companies', data);
    return response.data.data || response.data;
  }

  async deleteCompany(id: number): Promise<void> {
    await this.client.delete(`/companies/${id}`);
  }

  // Company Admin compliance methods
  async getCompanyEvidence(): Promise<any[]> {
    const response = await this.client.get('/compliance/execution/company/evidence');
    return response.data.data || response.data || [];
  }

  async getCompanyFrameworks(): Promise<CompanyFramework[]> {
    const response = await this.client.get('/compliance/execution/company/frameworks');
    return response.data.data || response.data || [];
  }

  async getSubmittedPagesForFramework(frameworkId: number): Promise<SubmittedPage[]> {
    const response = await this.client.get(`/compliance/execution/company/frameworks/${frameworkId}/submitted`);
    return response.data.data || response.data || [];
  }

  async getCompliancePageForReview(pageId: number): Promise<CompliancePageForReview> {
    const response = await this.client.get(`/compliance/execution/company/pages/${pageId}/review`);
    return response.data.data || response.data;
  }

  async approvePageForAudit(pageId: number): Promise<void> {
    await this.client.post(`/compliance/execution/company/pages/${pageId}/approve`);
  }

  async requestChanges(pageId: number, comments: string): Promise<void> {
    await this.client.post(`/compliance/execution/company/pages/${pageId}/request-changes`, { comments });
  }

  /**
   * Get dashboard stats for company admin
   */
  async getDashboardStats(companyId: number): Promise<CompanyDashboardStats> {
    const response = await this.client.get(`/companies/dashboard/stats?company_id=${companyId}`);
    return response.data.data;
  }


}

export const companiesApi = new CompaniesApi();
export default companiesApi;
export type { Company, UpdateCompanyRequest, CompanyFramework, CompanyDashboardStats, SubmittedPage, CompliancePageForReview };
